import type { Suggestion, Preview } from '../../../../shared/picker';
import { NetworkError } from '../../data/api';
import { autocomplete, pickerStatus, placeDetails } from '../../data/picker';
import { listLeads } from '../../data/store';
import { btn, linkBtn } from '../../ui/components';
import { h } from '../../ui/dom';
import { PICKER_COPY, pickerLedgerLabel, pickerOffLabel } from '../../ui/labels';
import { googleAttribution, suggestBox } from '../../ui/suggestBox';
import type { SuggestFailure, SuggestRow } from '../../ui/suggestBox';
import './placePicker.css';

type Row = Suggestion & SuggestRow;

const SESSION_MS = 180_000;
const UNAVAILABLE = PICKER_COPY.unavailable;

/**
 * Provider content has one owner: this disposable component. Only an ID crosses its boundary.
 * The look, timing and keyboard are the shared combobox's (D122); the D110 proxy, its UUID
 * session, the ledger line, the duplicate preflight and the binding stay here.
 */
export const placePicker = (
  initial: string | null,
  leadId: string | undefined,
  onBinding: (id: string | null) => void,
) => {
  let generation = 0,
    disposed = false,
    token = crypto.randomUUID(),
    expires = 0;
  let expiry: ReturnType<typeof setTimeout> | undefined,
    request: AbortController | undefined,
    point: Preview | undefined;

  const binding = h(
    'p',
    {},
    initial ? 'Business linked. Address is entered independently.' : 'No business selected',
  );
  const previewHost = h('div');
  // The preview outlives the rows, so it carries its own logo pair (the box's hides with them).
  const attribution = googleAttribution('place-picker__attribution');

  const abort = () => {
    request?.abort();
    request = undefined;
  };
  const clearPreview = () => {
    point = undefined;
    previewHost.replaceChildren();
    attribution.hidden = true;
  };
  /** Ends whatever is in flight and the session: the next search starts a new UUID. The preview
   *  is the binding's receipt and stays (CAY-124): only a new search (3+ characters), a new
   *  pick, Clear business binding or closing the form drops it. */
  const endSession = () => {
    generation++;
    abort();
    clearTimeout(expiry);
    expires = 0;
  };
  const stop = () => {
    endSession();
    clearPreview();
  };
  /** Three idle minutes: rows from the old session close, its token is never reused (D110). */
  const armExpiry = () => {
    clearTimeout(expiry);
    expiry = setTimeout(() => {
      box.reset();
      endSession();
    }, SESSION_MS);
  };
  const newSession = () => {
    token = crypto.randomUUID();
    expires = Date.now() + SESSION_MS;
    armExpiry();
  };

  const refresh = async () => {
    try {
      const s = await pickerStatus();
      if (disposed) return;
      box.setStatus(s.enabled ? pickerLedgerLabel(s) : pickerOffLabel(s));
      box.setDisabled(!s.enabled);
    } catch {
      if (!disposed) box.setStatus(UNAVAILABLE);
    }
  };

  const fetchRows = async (query: string): Promise<readonly Row[] | SuggestFailure> => {
    abort();
    // A completed session (a pick) or three idle minutes: never reuse the token (D110).
    if (Date.now() >= expires) newSession();
    const mine = new AbortController();
    request = mine;
    try {
      const rows = await autocomplete(query, token, mine.signal);
      return rows.map((r) => ({ ...r, main: r.name, secondary: r.address }));
    } catch (err) {
      return err instanceof NetworkError ? { kind: 'offline' } : { kind: 'error' };
    } finally {
      if (request === mine) request = undefined;
      if (!disposed && !mine.signal.aborted) void refresh();
    }
  };

  const pick = async (row: Row): Promise<boolean> => {
    const currentToken = token;
    abort();
    clearPreview();
    const mine = ++generation;
    const ctrl = new AbortController();
    request = ctrl;
    // Commit the new binding only after Details succeeds; failed or cancelled choices preserve it.
    try {
      const existing = (await listLeads()).find(
        (l) => l.place_id === row.place_id && l.id !== leadId,
      );
      if (disposed || mine !== generation) return true;
      if (existing) {
        binding.replaceChildren(
          h('span', {}, 'Already exists — '),
          linkBtn('Open existing lead', `#/leads/${encodeURIComponent(existing.id)}`),
        );
        // No Details call, so the session is still open: the rows come back under their own
        // token, and another row can complete it (the server only lets Details claim a token an
        // Autocomplete opened). Their three-minute expiry keeps running (CAY-124).
        return false;
      }
      const data = await placeDetails(row.place_id, currentToken, ctrl.signal);
      if (disposed || mine !== generation) return true;
      point = data;
      attribution.hidden = false;
      onBinding(data.id);
      binding.textContent = 'Business linked. Type name, street, city and ZIP independently below.';
      previewHost.append(
        h(
          'p',
          {},
          `Temporary Google preview: ${point.formattedAddress} (${point.location.latitude}, ${point.location.longitude}). Location still needs independent confirmation.`,
        ),
      );
      // Details completed the session on the server: the next search starts a new one.
      expires = 0;
      armExpiry();
      return true;
    } catch {
      // The message stays on the binding line: the box clears its own status when this returns.
      if (mine === generation && !disposed)
        binding.textContent = UNAVAILABLE;
      return false;
    } finally {
      if (request === ctrl) request = undefined;
      if (!disposed) void refresh();
    }
  };

  const inputId = `place-picker-${crypto.randomUUID()}`;
  const box = suggestBox<Row>({
    id: inputId,
    placeholder: 'Search business (3+ characters)',
    listLabel: 'Business matches',
    copy: {
      searching: 'Searching…',
      noMatch: PICKER_COPY.noMatch,
      offline: PICKER_COPY.offline,
      error: UNAVAILABLE,
      picking: 'Checking business…',
    },
    fetch: fetchRows,
    pick,
  });
  box.setStatus(UNAVAILABLE);
  // Merely typing a new query does not silently clear an existing saved binding, but it does
  // cancel the request in flight on the keystroke itself (D110). The preview goes only once the
  // query can search (3+ characters): clearing the field keeps the receipt (CAY-124).
  box.input.addEventListener('input', () => {
    generation++;
    abort();
    if (box.input.value.trim().length >= 3) clearPreview();
  });
  box.input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') endSession();
  });

  const manual = btn('Business not listed? Add manually', {
    onClick: () => {
      endSession();
      box.reset();
      box.input.value = '';
      box.setStatus('Type name, street, city and ZIP below. Save works offline.');
    },
  });
  const clear = btn('Clear business binding', {
    variant: 'subtle',
    onClick: () => {
      stop();
      box.reset();
      box.input.value = '';
      onBinding(null);
      binding.textContent = 'No business selected';
    },
  });
  // A real <label for>: tapping it focuses the field, and the name is said once (CAY-124).
  const root = h(
    'section',
    { class: 'card place-picker' },
    h(
      'div',
      { class: 'field' },
      h('label', { class: 'field__label', htmlFor: inputId }, 'Find a business'),
      box.root,
    ),
    h('div', { class: 'place-picker__provider' }, previewHost, attribution),
    binding,
    manual,
    clear,
  );
  void refresh();
  return {
    root,
    query: box.input,
    dispose: () => {
      disposed = true;
      stop();
      box.dispose();
      box.input.value = '';
    },
  };
};
